import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toastr } from 'react-redux-toastr';

import { openClientSession } from '../services/sessionService';
import { useAdmin } from './AdminContext';
import { adminApi, can } from './adminApi';
import { ActivityPanel, ConfirmDialog, ReasonDialog, TemporaryPasswordDialog } from './dialogs';
import { ActionsMenu, DescList, EmptyState, PageHeader, Panel } from './kit';
import { PlateTag, documentLabel, formatDay, formatDocument, formatPhoneBR, formatRelative } from './ui';

// Pagina do cliente: dados (mascarados ate "ver dados completos"), garagem e atividade.
export default function UserPage() {
  const { id } = useParams();
  const { admin } = useAdmin();
  const [user, setUser] = useState(null);
  const [failed, setFailed] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [activityKey, setActivityKey] = useState(0);

  useEffect(() => {
    let active = true;
    setUser(null);
    setRevealed(null);
    adminApi.user(id)
      .then((data) => { if (active) setUser({ ...data, vehicles: Array.isArray(data && data.vehicles) ? data.vehicles : [] }); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [id]);

  const crumbs = [{ label: 'Clientes', to: '/admin/users' }];
  const done = () => setActivityKey((key) => key + 1);

  if (failed) {
    return (
      <div className='ax-page'>
        <PageHeader crumbs={crumbs} title='Cliente' />
        <EmptyState title='Cliente não encontrado' text='Ele pode ter sido removido ou o endereço está incompleto.' action={<Link className='ax-btn' to='/admin/users'>Voltar para clientes</Link>} />
      </div>
    );
  }
  if (!user) {
    return (
      <div className='ax-page'>
        <PageHeader crumbs={crumbs} title='Carregando…' />
        <span className='ax-skeleton-block' aria-busy='true' />
      </div>
    );
  }

  const data = revealed || user;
  const name = user.name || 'Sem nome';

  const openViewAs = async (reason) => {
    // A aba abre no clique: depois do await o navegador bloquearia o pop-up.
    const tab = window.open('', '_blank');
    try {
      const session = await adminApi.userViewAs(user.id, reason);
      openClientSession({ name: session.name, expiresAt: session.expiresAt });
      localStorage.setItem('USER_CONFIRMED', 'true');
      if (tab) tab.location.href = '/client/dashboard';
      else window.location.href = '/client/dashboard';
      done();
    } catch (err) {
      if (tab) tab.close();
      throw err;
    }
  };

  const menu = [
    !revealed && { label: 'Ver dados completos', hint: 'Pede motivo', onSelect: () => setDialog('reveal') },
    can(admin, 'support_agent') && { label: 'Enviar link de nova senha', disabled: !user.hasEmail, hint: user.hasEmail ? null : 'Sem e-mail cadastrado', onSelect: () => setDialog('link') },
    can(admin, 'super_admin') && { label: 'Gerar senha provisória', danger: true, onSelect: () => setDialog('temp') },
  ];

  return (
    <div className='ax-page'>
      <PageHeader
        crumbs={crumbs}
        title={name}
        meta={`Cliente desde ${formatDay(user.createdAt)} · ID ${user.id}`}
        actions={(
          <>
            {can(admin, 'support_agent') && (
              <button type='button' className='ax-btn ax-btn-primary' onClick={() => setDialog('viewas')}>Ver como cliente</button>
            )}
            <ActionsMenu items={menu} />
          </>
        )}
      />

      {user.mustChangePassword && <p className='ax-alert is-warn'>Senha provisória ativa: o cliente vai criar uma senha nova no próximo acesso.</p>}

      <div className='ax-grid'>
        <Panel title='Dados' action={revealed ? <span className='ax-badge'>Dados completos · registrado</span> : null}>
          <DescList items={[
            { label: 'Nome', value: name },
            { label: 'E-mail', value: data.email },
            { label: 'Celular', value: data.phone ? formatPhoneBR(data.phone) : null },
            { label: documentLabel(data.cpfCnpj), value: data.cpfCnpj ? formatDocument(data.cpfCnpj) : null },
            { label: 'Cadastro', value: `${formatDay(user.createdAt)} (${formatRelative(user.createdAt)})` },
          ]} />
        </Panel>

        <Panel title={`Garagem (${user.vehicles.length})`}>
          {user.vehicles.length === 0 ? <p className='ax-muted'>Nenhum veículo cadastrado.</p> : (
            <ul className='ax-links'>
              {user.vehicles.map((vehicle) => (
                <li key={vehicle.id}>
                  <Link to={`/admin/maintenances/${vehicle.licensePlate}`}>
                    <PlateTag value={vehicle.licensePlate} />
                    <span>{[vehicle.brand, vehicle.model].filter(Boolean).join(' ') || 'Modelo não informado'}</span>
                    <span className='ax-link-hint'>Ver manutenções</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <ActivityPanel targetType='user' targetId={String(user.id)} refreshKey={activityKey} />

      {dialog === 'viewas' && (
        <ReasonDialog
          title={`Ver como ${name}?`}
          text='Abre a garagem do cliente numa aba nova, exatamente como ele vê, somente leitura e por 30 minutos. Se você estiver logado como cliente neste navegador, essa sessão será substituída.'
          confirmLabel='Abrir visualização'
          placeholder='Ex.: ticket 1234, cliente diz que a manutenção não aparece'
          onConfirm={openViewAs}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'reveal' && (
        <ReasonDialog
          title='Ver dados completos?'
          text={`E-mail, celular e documento de ${name} aparecem sem máscara nesta página.`}
          confirmLabel='Ver dados'
          onConfirm={async (reason) => { setRevealed({ ...user, ...(await adminApi.userReveal(user.id, reason)) }); done(); }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'link' && (
        <ConfirmDialog
          title={`Enviar link de nova senha para ${name}?`}
          text='O e-mail cadastrado recebe um link para criar uma senha nova. A senha atual continua valendo até ser trocada.'
          confirmLabel='Enviar link'
          onConfirm={async () => { const { sentTo } = await adminApi.userResetLink(user.id); toastr.success('Link enviado.', `Para ${sentTo}.`); done(); }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'temp' && (
        <TemporaryPasswordDialog
          targetName={name}
          onGenerate={async (reason, password) => { const result = await adminApi.userTemporaryPassword(user.id, reason, password); done(); return result; }}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
